import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { markOnboardingComplete } from '../../lib/api/onboarding';
import type {
  TourConfig,
  TourContextValue,
  TourGateContext,
  TourState,
  TourStorageKey,
} from './types';

interface TourProviderProps {
  /** Ordered list of tours; the first one whose gate is satisfied and whose
   *  completion flag is null wins. */
  tours: TourConfig[];
  /** Minimum viewport width to run any tour. Defaults to 768. */
  minViewportWidth?: number;
  children: ReactNode;
}

type Action =
  | { type: 'arm'; tourIndex: number }
  | { type: 'start' }
  | { type: 'next' }
  | { type: 'complete' }
  | { type: 'reset' };

const INITIAL_STATE: TourState = { status: 'idle', currentStep: 0, activeTourIndex: -1 };

function reducer(state: TourState, action: Action): TourState {
  switch (action.type) {
    case 'arm':
      return state.status === 'idle'
        ? { status: 'pending', currentStep: 0, activeTourIndex: action.tourIndex }
        : state;
    case 'start':
      return state.status === 'pending'
        ? { status: 'active', currentStep: 0, activeTourIndex: state.activeTourIndex }
        : state;
    case 'next':
      return state.status === 'active'
        ? { ...state, currentStep: state.currentStep + 1 }
        : state;
    case 'complete':
      return { ...state, status: 'complete' };
    case 'reset':
      return INITIAL_STATE;
    default:
      return state;
  }
}

/** Maps a tour's storageKey to the matching AuthUser column. */
function isTourCompleted(
  user: { onboardingCompletedAt: string | null; editOnboardingCompletedAt: string | null },
  storageKey: TourStorageKey
): boolean {
  if (storageKey === 'library') return user.onboardingCompletedAt !== null;
  return user.editOnboardingCompletedAt !== null;
}

export const TourContext = createContext<TourContextValue | null>(null);

export function TourProvider({ tours, minViewportWidth = 768, children }: TourProviderProps) {
  const { user, isLoading } = useAuth();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  // Anchors live in a ref, not state. The previous `useState<Map>` design
  // had a self-perpetuating loop under React 19: every register/unregister
  // call called setAnchors → TourProvider re-rendered → its children
  // re-rendered → any Radix `Slot` in a child re-ran `composeRefs(...)` →
  // a fresh ref callback was attached to the trigger button → React 19
  // detached/attached the ref → useTourAnchor's callback fired again →
  // setAnchors fired again → loop until React's #185 cap. By switching
  // to a ref, register/unregister mutate silently. We bump
  // `anchorRegistryVersion` (a counter) only when something the active
  // overlay actually cares about changes, so the overlay re-renders on
  // real transitions but the rest of the tree stays still.
  const anchorsRef = useRef<Map<string, HTMLElement>>(new Map());
  const [anchorRegistryVersion, setAnchorRegistryVersion] = useState(0);
  // In-session completion memory. We can't rely on AuthUser's flags right
  // after complete() — the PATCH is fire-and-forget and the /me refetch is
  // throttled to a 30s interval. Without this, the arm effect would re-arm
  // the same tour on the next render and loop forever.
  const [completedKeys, setCompletedKeys] = useState<Set<TourStorageKey>>(() => new Set());
  const completingRef = useRef(false);

  const registerAnchor = useCallback((name: string, element: HTMLElement) => {
    const current = anchorsRef.current.get(name);
    if (current !== element) {
      anchorsRef.current.set(name, element);
      // Defer the version bump so we don't dispatch state inside the ref
      // callback's synchronous path (which is what triggered the loop).
      queueMicrotask(() => setAnchorRegistryVersion((v) => v + 1));
    }
    return () => {
      if (anchorsRef.current.get(name) === element) {
        anchorsRef.current.delete(name);
        queueMicrotask(() => setAnchorRegistryVersion((v) => v + 1));
      }
    };
  }, []);

  // Build the gate context once per render dependency change.
  const gateCtx: TourGateContext | null = useMemo(() => {
    if (isLoading || !user) return null;
    const viewportOk =
      typeof window === 'undefined' || window.innerWidth >= minViewportWidth;
    return { user, pathname, searchParams, viewportOk };
  }, [isLoading, user, pathname, searchParams, minViewportWidth]);

  // Arm: when no tour active, pick the first ungated + uncompleted tour and arm it.
  useEffect(() => {
    if (state.status !== 'idle') return;
    if (!gateCtx) return;
    const idx = tours.findIndex(
      (t) =>
        t.gate.shouldRun(gateCtx) &&
        !isTourCompleted(gateCtx.user, t.gate.storageKey) &&
        !completedKeys.has(t.gate.storageKey)
    );
    if (idx >= 0) {
      completingRef.current = false;
      dispatch({ type: 'arm', tourIndex: idx });
    }
  }, [state.status, gateCtx, tours, completedKeys]);

  const activeTour = state.activeTourIndex >= 0 ? tours[state.activeTourIndex] : null;

  // Start: when pending and the first step's anchor is registered, fire it.
  // (Conditional anchors deeper in the tour are allowed to be missing — the
  // tooltip stays invisible until they mount, which is the pause-and-resume
  // behavior for the edit tour's changelog/submit-for-review steps.)
  useEffect(() => {
    if (state.status !== 'pending' || !activeTour) return;
    const firstStep = activeTour.steps[0];
    if (firstStep && anchorsRef.current.has(firstStep.anchorName)) {
      dispatch({ type: 'start' });
    }
  }, [state.status, activeTour, anchorRegistryVersion]);

  // Pause: if the active tour's gate stops being satisfied (e.g. user navigates
  // off /library), reset back to idle. The next render will re-evaluate gates
  // and may arm a different tour or wait.
  useEffect(() => {
    if (state.status !== 'active' || !activeTour || !gateCtx) return;
    if (!activeTour.gate.shouldRun(gateCtx)) {
      dispatch({ type: 'reset' });
    }
  }, [state.status, activeTour, gateCtx]);

  const step = useMemo(() => {
    if (state.status !== 'active' || !activeTour) return null;
    return activeTour.steps[state.currentStep] ?? null;
  }, [state, activeTour]);

  // Re-read on every render — but include anchorRegistryVersion in the
  // value-memo deps so consumers see the latest anchor when it changes,
  // and only then.
  const anchor = step ? anchorsRef.current.get(step.anchorName) ?? null : null;

  const next = useCallback(() => dispatch({ type: 'next' }), []);

  const complete = useCallback(() => {
    if (completingRef.current || !activeTour) return;
    completingRef.current = true;
    const key = activeTour.gate.storageKey;
    // Record locally BEFORE the server round-trip so the next render's arm
    // effect skips this tour even though gateCtx.user still says null.
    setCompletedKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    dispatch({ type: 'complete' });
    markOnboardingComplete({ tour: key }).catch(() => {
      /* swallowed — next session will retry naturally */
    });
  }, [activeTour]);

  // Esc key dismisses even when no tooltip is rendered (anchor missing).
  // The TourTooltip's onEscapeKeyDown also handles Esc when visible; this
  // is the fallback for the pause-and-resume gap.
  useEffect(() => {
    if (state.status !== 'active') return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        complete();
      }
    };
    document.addEventListener('keydown', handler, { capture: true });
    return () => document.removeEventListener('keydown', handler, { capture: true } as EventListenerOptions);
  }, [state.status, complete]);

  // When the active tour terminates, drop back to idle so the next ungated
  // tour can arm on the next render.
  useEffect(() => {
    if (state.status === 'complete') {
      const t = setTimeout(() => dispatch({ type: 'reset' }), 0);
      return () => clearTimeout(t);
    }
  }, [state.status]);

  const value: TourContextValue = useMemo(
    () => ({
      ...state,
      config: activeTour,
      step,
      anchor,
      registerAnchor,
      arm: () => {
        /* exposed for tests/dev — provider arms automatically based on gates */
      },
      next,
      complete,
    }),
    // Note: anchorRegistryVersion is intentionally NOT a dep. It exists only
    // to force TourProvider itself to re-render after register/unregister so
    // that `anchor` (read from anchorsRef.current) re-evaluates. Consumers
    // only need to re-render when `anchor` actually changes — which the
    // existing dep on `anchor` handles. Adding the version here would
    // re-render every consumer on every anchor mutation, re-creating the
    // exact loop we just escaped.
    [state, activeTour, step, anchor, registerAnchor, next, complete]
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}
